import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, UserX, UserCheck, KeyRound, Copy } from "lucide-react";

export default function AdminStaff() {
  const { t } = useTranslation();
  const [staff, setStaff] = useState([]);
  const [show, setShow] = useState(false);
  const [f, setF] = useState({ name: "", email: "", password: "", role: "clinician" });
  const [resetting, setResetting] = useState(null); // staff row being reset
  const [tempPw, setTempPw] = useState("");
  const [issued, setIssued] = useState(null); // { email, temp_password } after success

  const refresh = () => api.get("/admin/staff").then((r) => setStaff(r.data));
  useEffect(() => { refresh(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/staff", f);
      toast.success("Staff member added");
      setF({ name: "", email: "", password: "", role: "clinician" });
      setShow(false); refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const deactivate = async (id) => {
    if (!confirm("Deactivate this staff member?")) return;
    try { await api.post(`/admin/staff/${id}/deactivate`); toast.success("Deactivated"); refresh(); }
    catch { toast.error("Failed"); }
  };
  const activate = async (id) => {
    try { await api.post(`/admin/staff/${id}/activate`); toast.success("Activated"); refresh(); }
    catch (err) { toast.error(err.response?.data?.detail || "Activate failed"); }
  };
  const verifyHcpc = async (id) => {
    try { await api.post(`/admin/staff/${id}/verify-hcpc`); toast.success("HCPC verified"); refresh(); }
    catch (err) { toast.error(err.response?.data?.detail || "Verify failed"); }
  };
  const submitReset = async (e) => {
    e.preventDefault();
    if (!resetting) return;
    try {
      const { data } = await api.post(`/admin/staff/${resetting.id}/reset-temp-password`, {
        temp_password: tempPw.trim() || null,
      });
      setIssued({ email: data.email, temp_password: data.temp_password });
      setResetting(null); setTempPw("");
      refresh();
      toast.success("Temporary password reset");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    }
  };
  const copyPw = async () => {
    try { await navigator.clipboard.writeText(issued.temp_password); toast.success("Copied"); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <AdminLayout>
      <div className="flex justify-between items-end mb-8 gap-3 flex-wrap">
        <div>
          <div className="fl-label text-[#f47a20]">{t("pages.aStaff.eyebrow")}</div>
          <h1 className="fl-heading text-3xl md:text-4xl mt-2">{staff.length} {t("pages.aStaff.membersSuffix")}</h1>
        </div>
        <button onClick={() => setShow(!show)} className="fl-btn" data-testid="staff-add-btn"><UserPlus size={14} /> New staff</button>
      </div>

      {show && (
        <form onSubmit={submit} className="fl-card p-6 mb-6 grid md:grid-cols-2 gap-3">
          <input required placeholder="Full name" className="fl-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="staff-name" />
          <input required type="email" placeholder="Email" className="fl-input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="staff-email" />
          <input required type="password" placeholder="Temporary password" className="fl-input" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="staff-password" />
          <select className="fl-input" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="staff-role">
            <option value="clinician">Clinician</option>
            <option value="admin">Admin</option>
          </select>
          <button className="fl-btn md:col-span-2 justify-center" data-testid="staff-save">Create</button>
        </form>
      )}

      <div className="fl-card overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-4 p-4 border-b border-white/10 fl-label">
          <div className="col-span-4">Name</div>
          <div className="col-span-4">Email</div>
          <div className="col-span-2">Role</div>
          <div className="col-span-2">Actions</div>
        </div>
        {staff.map((s) => (
          <div key={s.id} className="md:grid md:grid-cols-12 md:gap-4 flex flex-col gap-2 p-4 border-b border-white/5 text-sm md:items-center">
            <div className="md:col-span-4">
              <div className="text-white">{s.name}</div>
              {s.active === false && <div className="text-xs text-red-500">Deactivated</div>}
            </div>
            <div className="md:col-span-4 text-zinc-400 break-all">
              {s.email}
              {s.role === "clinician" && s.hcpc_number && (
                <div className="mt-0.5 text-[10px] flex items-center gap-1.5 flex-wrap">
                  <span className="text-zinc-500 uppercase tracking-widest">HCPC</span>
                  <span className="text-zinc-300 font-mono">{s.hcpc_number}</span>
                  {s.hcpc_verified ? (
                    <span className="px-1.5 py-0.5 rounded text-emerald-400 border border-emerald-500/30" data-testid={`staff-hcpc-verified-${s.id}`}>Verified</span>
                  ) : (
                    <button
                      onClick={() => verifyHcpc(s.id)}
                      className="px-1.5 py-0.5 rounded text-amber-400 border border-amber-500/40 hover:text-white hover:bg-amber-500/20"
                      data-testid={`staff-hcpc-verify-${s.id}`}
                      title="Confirm this HCPC number against the public register, then click to mark verified."
                    >
                      Pending Verification · Verify
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="md:col-span-2"><span className="text-xs px-2 py-0.5 rounded border border-white/10 uppercase inline-block">{s.role}</span></div>
            <div className="md:col-span-2 flex flex-wrap gap-3 items-center">
              {s.active !== false && s.id !== undefined && (
                <button
                  onClick={() => { setResetting(s); setTempPw(""); }}
                  className="text-xs text-[#f47a20] hover:text-orange-300 flex items-center gap-1"
                  data-testid={`staff-reset-pw-${s.id}`}
                >
                  <KeyRound size={12} /> Reset password
                </button>
              )}
              {s.active !== false && <button onClick={() => deactivate(s.id)} className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1" data-testid={`staff-deactivate-${s.id}`}><UserX size={12} /> Deactivate</button>}
              {s.active === false && <button onClick={() => activate(s.id)} className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1" data-testid={`staff-activate-${s.id}`}><UserCheck size={12} /> Activate</button>}
            </div>
          </div>
        ))}
      </div>

      {resetting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6" onClick={() => setResetting(null)}>
          <form onSubmit={submitReset} onClick={(e) => e.stopPropagation()} className="fl-card p-6 w-full max-w-md space-y-4" data-testid="staff-reset-modal">
            <div>
              <div className="fl-label text-[#f47a20]">Reset temporary password</div>
              <h2 className="fl-heading text-2xl mt-1">{resetting.name}</h2>
              <p className="text-xs text-zinc-500 mt-1">{resetting.email}</p>
            </div>
            <div>
              <div className="fl-label mb-2">Temporary password</div>
              <input
                className="fl-input"
                type="text"
                minLength={8}
                placeholder="Leave blank to auto-generate (12 chars)"
                value={tempPw}
                onChange={(e) => setTempPw(e.target.value)}
                data-testid="staff-reset-temp-input"
              />
              <div className="text-xs text-zinc-500 mt-1">
                On next login the user will be forced to choose a new password.
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setResetting(null)} className="text-sm text-zinc-400 hover:text-white" data-testid="staff-reset-cancel">
                Cancel
              </button>
              <button className="fl-btn" data-testid="staff-reset-confirm">
                <KeyRound size={14} /> Reset password
              </button>
            </div>
          </form>
        </div>
      )}

      {issued && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6" onClick={() => setIssued(null)}>
          <div onClick={(e) => e.stopPropagation()} className="fl-card p-6 w-full max-w-md space-y-4" data-testid="staff-reset-issued-modal">
            <div>
              <div className="fl-label text-[#f47a20]">Copy this password now</div>
              <h2 className="fl-heading text-2xl mt-1">Temporary password issued</h2>
              <p className="text-xs text-zinc-500 mt-1">{issued.email}</p>
            </div>
            <div className="fl-card p-4 flex items-center justify-between gap-4" style={{ background: "var(--fl-bg-2)" }}>
              <code className="text-sm text-white break-all" data-testid="staff-reset-issued-value">{issued.temp_password}</code>
              <button onClick={copyPw} className="fl-btn text-xs" data-testid="staff-reset-copy">
                <Copy size={12} /> Copy
              </button>
            </div>
            <div className="text-xs text-zinc-500">
              This password is shown once. Share it with the user through a secure channel — they'll be prompted to change it on next login.
            </div>
            <div className="flex justify-end">
              <button onClick={() => setIssued(null)} className="fl-btn" data-testid="staff-reset-done">Done</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
